import { HandleError } from './error-strategies';
export function ObjectPathGet(obj, path) {
    for (let i = 0, l = path.length; i < l; i++) {
        obj = obj[path[i]];
    }
    return obj;
}
export function ObjectPathSet(obj, path, value) {
    const last = path.length - 1;
    for (let i = 0; i < last; i++) {
        obj = obj[path[i]];
    }
    obj[last] = value;
}
function ObjectPathDelete(obj, path) {
    const last = path.length - 1;
    for (let i = 0; i < last; i++) {
        obj = obj[path[i]];
    }
    return delete (obj[last]);
}
export function ObjectPathExists(obj, path) {
    for (let i = 0, l = path.length; i < l; i++) {
        if (path[i] in obj) {
            obj = obj[path[i]];
        }
        else {
            return false;
        }
    }
    return true;
}
export function* GetPrototypeChain(target) {
    while (target !== null) {
        yield target;
        target = Object.getPrototypeOf(target);
    }
}
export function GetOwnProperties(target) {
    return Object.getOwnPropertyNames(target)
        .concat(Object.getOwnPropertySymbols(target));
}
export function* GetProperties(target) {
    const iterator = GetPrototypeChain(target);
    let result;
    while (!(result = iterator.next()).done) {
        yield* GetOwnProperties(result.value);
    }
}
export function HasOwnProperty(target, propertyKey) {
    return Object.prototype.hasOwnProperty.call(target, propertyKey);
}
export function HasProperty(target, propertyKey) {
    return (propertyKey in target);
}
export function GetOwnPropertyDescriptors(target) {
    return Object.entries(Object.getOwnPropertyDescriptors(target))[Symbol.iterator]();
}
export function* GetPropertyDescriptors(target) {
    const excludes = new Set([
        'constructor',
        '__proto__',
        '__defineGetter__',
        '__defineSetter__',
        '__lookupGetter__',
        '__lookupSetter__',
    ]);
    const properties = new Set();
    const iterator = GetPrototypeChain(target);
    let result;
    while (!(result = iterator.next()).done) {
        yield* GetOwnProperties(result.value)
            .filter((propertyKey) => {
            if (excludes.has(propertyKey) || properties.has(propertyKey)) {
                return false;
            }
            else {
                properties.add(propertyKey);
                return true;
            }
        })
            .map((propertyKey) => {
            return [propertyKey, Object.getOwnPropertyDescriptor(result.value, propertyKey)];
        });
    }
}
export function Implements(target, source, level = 'exists') {
    if (target === null) {
        return false;
    }
    else if (source === null) {
        return true;
    }
    else {
        let targetProperties = (level === 'exists')
            ? new Set(GetProperties(target))
            : void 0;
        const iterator = GetProperties(source);
        let result;
        while (!(result = iterator.next()).done) {
            switch (level) {
                case 'exists':
                    if (!targetProperties.has(result.value)) {
                        return false;
                    }
                    break;
                case 'type':
                    if (typeof target[result.value] !== typeof source[result.value]) {
                        return false;
                    }
                    break;
                case 'strict':
                    if (target[result.value] !== source[result.value]) {
                        return false;
                    }
                    break;
                default:
                    throw new TypeError(`Expected 'type' or 'strict' as level`);
            }
        }
        return true;
    }
}
export function MustImplement(target, source, level = 'exists') {
    let targetProperties = (level === 'exists')
        ? new Set(GetProperties(target))
        : void 0;
    const iterator = GetProperties(source);
    let result;
    while (!(result = iterator.next()).done) {
        switch (level) {
            case 'exists':
                if (!targetProperties.has(result.value)) {
                    throw new Error(`The source property '${result.value}' is missing in target`);
                }
                break;
            case 'type':
                if (typeof target[result.value] !== typeof source[result.value]) {
                    throw new Error(`The source property '${result.value}' has a different type in target`);
                }
                break;
            case 'strict':
                if (target[result.value] !== source[result.value]) {
                    throw new Error(`The source property '${result.value}' is not the same as in target`);
                }
                break;
            default:
                throw new TypeError(`Expected 'type' or 'strict' as level`);
        }
    }
}
export function GetPropertyDescriptor(target, propertyKey) {
    let descriptor;
    const iterator = GetPrototypeChain(target);
    let result;
    while (!(result = iterator.next()).done
        && ((descriptor = Object.getOwnPropertyDescriptor(result.value, propertyKey)) === void 0)) { }
    return descriptor;
}
export function CopyOwnDescriptors(source, destination, conflictStrategy) {
    Object.entries(Object.getOwnPropertyDescriptors(source)).forEach(([key, descriptor]) => {
        if (!HasOwnProperty(destination, key) || HandleError(() => new Error(`Property '${key}' already exists`), conflictStrategy)) {
            Object.defineProperty(destination, key, descriptor);
        }
    });
    return destination;
}
export function CopyDescriptors(source, destination, conflictStrategy) {
    const iterator = GetPropertyDescriptors(source);
    let result;
    while (!(result = iterator.next()).done) {
        const [key, descriptor] = result.value;
        if (!HasProperty(destination, key) || HandleError(() => new Error(`Property '${key}' already exists`), conflictStrategy)) {
            Object.defineProperty(destination, key, descriptor);
        }
    }
    return destination;
}
export function SetConstructor(target, _constructor) {
    Object.defineProperty(target, 'constructor', {
        value: _constructor,
        writable: true,
        configurable: true,
        enumerable: false,
    });
    return target;
}
export function SetFunctionName(target, name) {
    Object.defineProperty(target, 'name', Object.assign(Object.getOwnPropertyDescriptor(target, 'name'), { value: name }));
    return target;
}
//# sourceMappingURL=object-helpers.js.map